import { HttpError } from './HTTPErrors';

const MAX_ARGS_JSON_LENGTH = 2000;

/** Why {@link DataFetchError} was raised from {@link EnsureData} / {@link ensureQueryData}. */
export type DataFetchFailureReason =
  | 'missing-data'
  | 'query-error'
  | 'error-response'
  | 'unexpected-format';

/** React Query + request context captured when a data fetch fails in the UI layer. */
export type DataFetchDiagnosticContext = {
  queryName: string;
  queryArgs?: unknown;
  fetchStatus?: string;
  queryStatus?: string;
  isFetching?: boolean;
  isLoading?: boolean;
  reason: DataFetchFailureReason;
  originalErrorName?: string;
  pageUrl?: string;
  timestamp: string;
};

const USER_MESSAGES: Record<DataFetchFailureReason, string> = {
  'missing-data':
    'Data could not be loaded. The request finished without returning any data. Please try again.',
  'query-error':
    'Something went wrong while loading data. Please try again or contact support if the problem continues.',
  'error-response':
    'The server returned an error while loading data. Please try again.',
  'unexpected-format':
    'Received an unexpected response while loading data. Please try again.',
};

function truncateJson(value: unknown): unknown {
  if (value === undefined || value === null) {
    return value;
  }

  try {
    const json = JSON.stringify(value);
    if (json.length <= MAX_ARGS_JSON_LENGTH) {
      return value;
    }
    return `${json.slice(0, MAX_ARGS_JSON_LENGTH)}… (truncated)`;
  } catch {
    return '[non-serializable query args]';
  }
}

/**
 * Structured error raised when {@link EnsureData} cannot render because a query
 * settled without usable data or with an unclassified query error.
 *
 * Extends {@link HttpError} so error boundaries, Sentry, and {@link ErrorPage}
 * can correlate client-side failures with query context.
 */
export class DataFetchError extends HttpError {
  readonly diagnostics: DataFetchDiagnosticContext;

  constructor(
    diagnostics: Omit<DataFetchDiagnosticContext, 'timestamp' | 'pageUrl'> & {
      timestamp?: string;
      pageUrl?: string;
    },
    options?: {
      message?: string;
      code?: number;
      cause?: Error;
      requestId?: string;
    },
  ) {
    const message = options?.message ?? USER_MESSAGES[diagnostics.reason];
    const code = options?.code ?? 500;

    super(code, message, 'DataFetchError', {
      requestId: options?.requestId ?? globalThis.crypto.randomUUID(),
    });

    this.diagnostics = {
      ...diagnostics,
      queryArgs: truncateJson(diagnostics.queryArgs),
      timestamp: diagnostics.timestamp ?? new Date().toISOString(),
      pageUrl:
        diagnostics.pageUrl ??
        (typeof globalThis.location !== 'undefined'
          ? globalThis.location.href
          : undefined),
    };

    if (options?.cause !== undefined) {
      (this as Error & { cause?: Error }).cause = options.cause;
    }

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Type guard for {@link DataFetchError}. */
export function isDataFetchError(error: unknown): error is DataFetchError {
  return error instanceof DataFetchError;
}

/**
 * Builds a {@link DataFetchError} for EnsureData failure paths.
 */
export function createDataFetchError(
  diagnostics: Omit<DataFetchDiagnosticContext, 'timestamp' | 'pageUrl'>,
  options?: {
    message?: string;
    code?: number;
    cause?: Error;
    requestId?: string;
  },
): DataFetchError {
  return new DataFetchError(diagnostics, options);
}

/**
 * When a query error is not already an {@link HttpError}, wrap it so ErrorPage
 * can show a safe message while preserving the original cause.
 */
export function toQueryHttpError(
  error: unknown,
  diagnostics: Omit<
    DataFetchDiagnosticContext,
    'timestamp' | 'pageUrl' | 'reason'
  >,
): HttpError | DataFetchError {
  if (error instanceof HttpError) {
    return error;
  }

  if (isDataFetchError(error)) {
    return error;
  }

  let message: string | undefined;
  let originalErrorName: string | undefined;
  let cause: Error | undefined;

  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
    originalErrorName = error.name;
    cause = error;
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    const err = error as { name?: string; message: string };
    message = `[${err.name ?? 'Error'}] ${err.message}`;
    originalErrorName = err.name;
    cause = new Error(err.message);
  }

  return createDataFetchError(
    {
      ...diagnostics,
      reason: 'query-error',
      originalErrorName,
    },
    {
      message,
      cause,
    },
  );
}
